<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ApexOptions, ApexTooltipCustomOpts } from 'apexcharts'
import VueApexCharts from 'vue3-apexcharts'
import { useTheme } from '@/composables/useTheme'
import { cn } from '@/utils/cn'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import ChartCard from './ChartCard.vue'
import type { EntityYearSeries } from '@/composables/useDashboardData'

const props = defineProps<{
  title: string
  rows: EntityYearSeries[]
}>()

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const BRAND_COLOR = '#465FFF'
const BRAND_GRADIENT_FROM = '#7592FF'

const { theme } = useTheme()
const isDarkMode = computed(() => theme.value === 'dark')

const selectedYear = ref<number | null>(null)

const activeYear = computed(() => {
  if (props.rows.length === 0) return null
  if (selectedYear.value !== null && props.rows.some((row) => row.year === selectedYear.value)) {
    return selectedYear.value
  }
  return props.rows[props.rows.length - 1].year
})

const values = computed(
  () => props.rows.find((row) => row.year === activeYear.value)?.values ?? Array(12).fill(0),
)

const series = computed(() => [{ name: props.title, data: values.value }])

const options = computed<ApexOptions>(() => ({
  colors: [BRAND_COLOR],
  chart: {
    fontFamily: 'Outfit, sans-serif',
    type: 'bar',
    height: 200,
    toolbar: {
      show: false,
    },
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '38%',
      borderRadius: 8,
      borderRadiusApplication: 'around',
      dataLabels: {
        position: 'top',
      },
    },
  },
  dataLabels: {
    enabled: true,
    formatter: (val: number) => (val > 0 ? String(val) : ''),
    offsetY: -20,
    style: {
      fontSize: '12px',
      fontFamily: 'Outfit, sans-serif',
      fontWeight: 400,
      colors: [isDarkMode.value ? '#94A3B8' : '#364153'],
    },
    dropShadow: {
      enabled: false,
    },
  },
  stroke: {
    show: false,
  },
  xaxis: {
    categories: MONTH_LABELS,
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: {
        colors: isDarkMode.value ? '#94A3B8' : '#364153',
        fontFamily: 'Outfit, sans-serif',
        fontSize: '12px',
      },
    },
  },
  legend: {
    show: false,
  },
  yaxis: {
    labels: {
      show: false,
    },
    tickAmount: 4,
  },
  grid: {
    borderColor: isDarkMode.value ? '#1E2640' : '#F3F4F6',
    strokeDashArray: 0,
    padding: {
      left: -10,
      right: 0,
    },
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: false,
      },
    },
  },
  fill: {
    type: 'gradient',
    gradient: {
      type: 'vertical',
      shadeIntensity: 1,
      opacityFrom: 1,
      opacityTo: 1,
      stops: [0, 100],
      colorStops: [
        {
          offset: 0,
          color: BRAND_GRADIENT_FROM,
          opacity: 1,
        },
        {
          offset: 100,
          color: BRAND_COLOR,
          opacity: 1,
        },
      ],
    },
  },
  tooltip: {
    enabled: true,
    custom: function ({ series: chartSeries, dataPointIndex, w }: ApexTooltipCustomOpts) {
      const category =
        w.globals?.categoryLabels?.[dataPointIndex] ??
        w.config.xaxis?.categories?.[dataPointIndex] ??
        ''
      const value = chartSeries[0]?.[dataPointIndex] ?? ''

      return `
        <div class="min-w-32.5">
          <p class="mb-1 text-[10px] leading-4 text-gray-500 dark:text-gray-400">${category}</p>
          <div class="flex items-center gap-1.5">
            <span class="size-1.5 shrink-0 rounded-full" style="background-color: ${BRAND_COLOR}"></span>
            <span class="w-13 text-xs text-gray-800 dark:text-white/90">${props.title}</span>
            <span class="text-xs font-medium text-gray-400 dark:text-gray-500">:</span>
            <span class="text-xs font-medium text-gray-800 dark:text-white/90">${value}</span>
          </div>
        </div>
      `
    },
  },
}))
</script>

<template>
  <ChartCard :title="title">
    <template #action>
      <ButtonGroup
        v-if="rows.length > 1"
        aria-label="Rok"
        class="max-w-full gap-0.5 overflow-x-auto rounded-lg bg-gray-100 p-0.5 *:data-slot:rounded-md! dark:bg-gray-900"
      >
        <Button
          v-for="row in rows"
          :key="row.year"
          variant="ghost"
          size="xs"
          :aria-pressed="activeYear === row.year"
          :class="
            cn(
              'h-8 px-3 text-sm font-medium transition-colors',
              activeYear === row.year
                ? 'bg-white text-gray-900 shadow-theme-xs hover:bg-white hover:text-gray-900 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-800 dark:hover:text-white'
                : 'text-gray-500 hover:bg-transparent hover:text-gray-900 dark:text-gray-400 dark:hover:bg-transparent dark:hover:text-white',
            )
          "
          @click="selectedYear = row.year"
        >
          {{ row.year }}
        </Button>
      </ButtonGroup>
    </template>

    <div class="max-w-full overflow-x-auto">
      <div class="min-w-162.5 xl:min-w-full">
        <VueApexCharts type="bar" :height="200" :options="options" :series="series" />
      </div>
    </div>
  </ChartCard>
</template>
